import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  User, 
  Calendar, 
  Brain, 
  GraduationCap, 
  Users, 
  BookOpen, 
  Plane, 
  Heart, 
  MapPin, 
  Building2, 
  Briefcase, 
  Award, 
  Upload,
  X,
  Loader2
} from 'lucide-react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import portfolioService from '@services/portfolio.service';
import Button from '@components/common/Button';

const portfolioSchema = z.object({
  age: z.number().min(15, 'Age must be at least 15').max(100, 'Age must be less than 100'),
  personalityTraits: z.array(z.number()).min(1, 'Select at least one personality trait').max(20, 'Maximum 20 traits allowed'),
  acquiredDegree: z.number().min(1, 'Please select a degree'),
  userStage: z.enum(['student', 'fresher', 'professional'], {
    required_error: 'Please select a user stage'
  }),
  coursePreference: z.enum(['short_term', 'long_term', 'certificate'], {
    required_error: 'Please select a course preference'
  }),
  abroadEducation: z.boolean(),
  medicalIssue: z.boolean(),
  state: z.string().min(1, 'Please select a state'),
  university: z.string().min(1, 'Please select a university or institute'),
  opportunities: z.enum(['govt', 'private'], {
    required_error: 'Please select opportunity type'
  }),
  examType: z.string().min(1, 'Please select exam type'),
  customExam: z.string().optional(),
  awardsAndHonours: z.string().optional(),
  certificates: z.array(z.any()).optional(),
  industry: z.number().min(1, 'Please select an industry')
});

const PortfolioForm = ({ onClose, onSuccess, isInline = false }) => {
  const user = useSelector((state) => state.auth.user);
  
  const [loading, setLoading] = useState(false);
  const [formDataLoading, setFormDataLoading] = useState(true);
  
  const [personalityTraits, setPersonalityTraits] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [states, setStates] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [industries, setIndustries] = useState([]);
  
  const [selectedState, setSelectedState] = useState('');
  const [selectedUserStage, setSelectedUserStage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(portfolioSchema),
    defaultValues: {
      age: 18,
      personalityTraits: [],
      acquiredDegree: 0,
      userStage: '',
      coursePreference: '',
      abroadEducation: false,
      medicalIssue: false,
      state: '',
      university: '',
      opportunities: '',
      examType: '',
      customExam: '',
      awardsAndHonours: '',
      certificates: [],
      industry: 0
    }
  });

  const watchState = watch('state');
  const watchUserStage = watch('userStage');
  const watchExamType = watch('examType');
  const watchPersonalityTraits = watch('personalityTraits');

  useEffect(() => {
    fetchFormData();
  }, []);

  useEffect(() => {
    if (watchState && watchState !== selectedState) {
      setSelectedState(watchState);
      fetchUniversities(watchState);
      setValue('university', '');
    }
  }, [watchState]);

  useEffect(() => {
    if (watchUserStage && watchUserStage !== selectedUserStage) {
      setSelectedUserStage(watchUserStage);
      fetchIndustries(watchUserStage);
      setValue('industry', 0);
    }
  }, [watchUserStage]);

  const fetchFormData = async () => {
    try {
      setFormDataLoading(true);
      const [traitsRes, degreesRes, statesRes] = await Promise.all([
        portfolioService.getPersonalityTraits(),
        portfolioService.getDegrees(),
        portfolioService.getStates()
      ]);

      if (traitsRes.success && Array.isArray(traitsRes.data)) {
        setPersonalityTraits(traitsRes?.data?.data);
      } else {
        setPersonalityTraits([]);
      }
      
      if (degreesRes.success && Array.isArray(degreesRes.data)) {
        setDegrees(degreesRes.data);
      } else {
        setDegrees([]);
      }
      
      if (statesRes.success && Array.isArray(statesRes.data)) {
        setStates(statesRes.data);
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load form data');
      setPersonalityTraits([]);
      setDegrees([]);
      setStates([]);
    } finally {
      setFormDataLoading(false);
    }
  };

  const fetchUniversities = async (state) => {
    try {
      const response = await portfolioService.getUniversitiesByState(state);
      if (response.success && Array.isArray(response.data)) {
        setUniversities(response.data);
      } else {
        setUniversities([]);
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
      toast.error('Failed to load universities');
      setUniversities([]);
    }
  };

  const fetchIndustries = async (userStage) => {
    try {
      const response = await portfolioService.getIndustriesByUserStage(userStage);
      if (response.success && Array.isArray(response.data)) {
        setIndustries(response.data);
      } else {
        setIndustries([]);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.error('Failed to load industries');
      setIndustries([]);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles((prev) => [...prev, ...files]);
    setValue('certificates', [...uploadedFiles, ...files]);
  };

  const removeFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    setValue('certificates', newFiles);
  };

  const togglePersonalityTrait = (traitId) => {
    const currentTraits = watchPersonalityTraits || [];
    const newTraits = currentTraits.includes(traitId)
      ? currentTraits.filter((id) => id !== traitId)
      : currentTraits.length < 20
      ? [...currentTraits, traitId]
      : currentTraits;
    
    setValue('personalityTraits', newTraits);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    
    const payload = {
      name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email,
      age: data.age,
      personalityTraits: data.personalityTraits,
      acquiredDegree: data.acquiredDegree,
      userStage: data.userStage,
      coursePreference: data.coursePreference,
      abroadEducation: data.abroadEducation,
      medicalIssue: data.medicalIssue,
      state: data.state,
      university: data.university,
      opportunities: data.opportunities,
      examType: data.examType,
      customExam: data.customExam || '',
      awardsAndHonours: data.awardsAndHonours || '',
      certificates: uploadedFiles,
      industry: data.industry,
      userId: user?.id
    };

    console.log('=== PORTFOLIO FORM PAYLOAD ===');
    console.log(JSON.stringify(payload, null, 2));
    console.log('==============================');

    toast.success('Portfolio setup completed successfully!');
    
    setLoading(false);
    
    if (onSuccess) {
      onSuccess(payload);
    }
  };

  if (formDataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary-500" />
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-lg ${isInline ? 'w-full' : 'max-w-4xl mx-auto'}`}>
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-primary-700">
          Portfolio Setup
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
        {/* Basic Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name (Read-only from logged-in user) */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4" />
              Name
            </label>
            <input
              type="text"
              value={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
            />
          </div>

          {/* Age */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4" />
              Age *
            </label>
            <input
              type="number"
              {...register('age', { valueAsNumber: true })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
              placeholder="Enter your age"
            />
            {errors.age && (
              <p className="mt-1 text-sm text-red-500">{errors.age.message}</p>
            )}
          </div>
        </div>

        {/* Personality Traits */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Brain className="w-4 h-4" />
            Personality Traits * (Max 20)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-60 overflow-y-auto p-4 border border-gray-300 rounded-lg">
            {personalityTraits.map((trait) => (
              <button
                key={trait.id}
                type="button"
                onClick={() => togglePersonalityTrait(trait.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  watchPersonalityTraits?.includes(trait.id)
                    ? 'bg-secondary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {trait.name}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Selected: {watchPersonalityTraits?.length || 0} / 20
          </p>
          {errors.personalityTraits && (
            <p className="mt-1 text-sm text-red-500">{errors.personalityTraits.message}</p>
          )}
        </div>

        {/* Education Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Acquired Degree */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <GraduationCap className="w-4 h-4" />
              Acquired Degree *
            </label>
            <select
              {...register('acquiredDegree', { valueAsNumber: true })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
            >
              <option value={0}>Select a degree</option>
              {degrees.map((degree) => (
                <option key={degree.id} value={degree.id}>
                  {degree.name}
                </option>
              ))}
            </select>
            {errors.acquiredDegree && (
              <p className="mt-1 text-sm text-red-500">{errors.acquiredDegree.message}</p>
            )}
          </div>

          {/* User Stage */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4" />
              User Stage *
            </label>
            <select
              {...register('userStage')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
            >
              <option value="">Select user stage</option>
              <option value="student">Student</option>
              <option value="fresher">Fresher</option>
              <option value="professional">Professional</option>
            </select>
            {errors.userStage && (
              <p className="mt-1 text-sm text-red-500">{errors.userStage.message}</p>
            )}
          </div>

          {/* Course Preference */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <BookOpen className="w-4 h-4" />
              Course Preference *
            </label>
            <select
              {...register('coursePreference')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
            >
              <option value="">Select course preference</option>
              <option value="short_term">Short Term</option>
              <option value="long_term">Long Term</option>
              <option value="certificate">Certificate</option>
            </select>
            {errors.coursePreference && (
              <p className="mt-1 text-sm text-red-500">{errors.coursePreference.message}</p>
            )}
          </div>
        </div>

        {/* Checkboxes Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Abroad Education */}
          <div className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg">
            <Plane className="w-5 h-5 text-secondary-500" />
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('abroadEducation')}
                className="w-4 h-4 text-secondary-500 border-gray-300 rounded focus:ring-secondary-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Abroad Education
              </span>
            </label>
          </div>

          {/* Medical Issue */}
          <div className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg">
            <Heart className="w-5 h-5 text-red-500" />
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('medicalIssue')}
                className="w-4 h-4 text-secondary-500 border-gray-300 rounded focus:ring-secondary-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Medical Issue
              </span>
            </label>
          </div>
        </div>

        {/* Location Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* State */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4" />
              State *
            </label>
            <select
              {...register('state')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
            >
              <option value="">Select a state</option>
              {states.map((state) => (
                <option key={state.id} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            {errors.state && (
              <p className="mt-1 text-sm text-red-500">{errors.state.message}</p>
            )}
          </div>

          {/* University or Institute */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4" />
              University or Institute *
            </label>
            <select
              {...register('university')}
              disabled={!watchState || universities.length === 0}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {!watchState
                  ? 'Please select a state first'
                  : universities.length === 0
                  ? 'No universities found for selected state'
                  : 'Select a university'}
              </option>
              {universities.map((uni) => (
                <option key={uni.id} value={uni.id}>
                  {uni.universityName}
                </option>
              ))}
            </select>
            {errors.university && (
              <p className="mt-1 text-sm text-red-500">{errors.university.message}</p>
            )}
          </div>
        </div>

        {/* Career Preferences Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Opportunities */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Briefcase className="w-4 h-4" />
              Opportunities *
            </label>
            <select
              {...register('opportunities')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
            >
              <option value="">Select opportunity type</option>
              <option value="govt">Government</option>
              <option value="private">Private</option>
            </select>
            {errors.opportunities && (
              <p className="mt-1 text-sm text-red-500">{errors.opportunities.message}</p>
            )}
          </div>

          {/* Exams */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Award className="w-4 h-4" />
              Exams *
            </label>
            <select
              {...register('examType')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
            >
              <option value="">Select exam type</option>
              <option value="state_admission">State Exam for Admission</option>
              <option value="national_admission">National Exam for Admission</option>
              <option value="state_job">State Exam for Job</option>
              <option value="center_job">Center Exam for Job</option>
              <option value="other">Other Exam</option>
              <option value="custom">Custom</option>
            </select>
            {errors.examType && (
              <p className="mt-1 text-sm text-red-500">{errors.examType.message}</p>
            )}
          </div>
        </div>

        {/* Custom Exam Input */}
        {(watchExamType === 'custom' || watchExamType === 'other') && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Specify Exam Name
            </label>
            <input
              type="text"
              {...register('customExam')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
              placeholder="Enter exam name"
            />
          </div>
        )}

        {/* Awards and Honours */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Award className="w-4 h-4" />
            Awards and Honours
          </label>
          <textarea
            {...register('awardsAndHonours')}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
            placeholder="Describe your awards, honours, and achievements..."
          />
        </div>

        {/* Certificate Upload */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Upload className="w-4 h-4" />
            Upload Certificates
          </label>
          <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              className="hidden"
              id="certificate-upload"
            />
            <label
              htmlFor="certificate-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              <Upload className="w-8 h-8 text-gray-400" />
              <span className="text-sm text-gray-600">
                Click to upload certificates (PDF, JPG, PNG)
              </span>
            </label>
          </div>
          {uploadedFiles.length > 0 && (
            <div className="mt-3 space-y-2">
              {uploadedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-100 rounded-lg"
                >
                  <span className="text-sm text-gray-700 truncate">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Choose Industry */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Briefcase className="w-4 h-4" />
            Choose Industry *
          </label>
          <select
            {...register('industry', { valueAsNumber: true })}
            disabled={!watchUserStage || industries.length === 0}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value={0}>
              {!watchUserStage
                ? 'Please select user stage first'
                : industries.length === 0
                ? 'No industries found for selected user stage'
                : 'Select an industry'}
            </option>
            {industries.map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.name}
              </option>
            ))}
          </select>
          {errors.industry && (
            <p className="mt-1 text-sm text-red-500">{errors.industry.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex gap-4 pt-4">
          <Button
            type="submit"
            disabled={loading}
            className="flex-1 bg-secondary-500 hover:bg-secondary-600 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Portfolio'
            )}
          </Button>
          {onClose && (
            <Button
              type="button"
              onClick={onClose}
              className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 rounded-lg transition-colors"
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PortfolioForm;
